import { Autocomplete,Button,IconButton,Skeleton,Stack,TextField,Tooltip,Typography } from '@mui/material';
import { createTheme,ThemeProvider } from '@mui/material/styles';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import '@mui/x-date-pickers/themeAugmentation';
import { useQuery } from '@tanstack/react-query';
import { ptBR } from 'date-fns/locale';
import html2canvas from 'html2canvas';
import MUIDataTable,{ MUIDataTableOptions } from 'mui-datatables';
import pdfMake from "pdfmake/build/pdfmake";
import { useState } from 'react';
import { downloadExcel } from 'react-export-table-to-excel';
import { useTranslation } from 'react-i18next';
import { FaFilePdf } from 'react-icons/fa';
import { IoFilterSharp } from "react-icons/io5";
import { RiFileExcel2Fill } from 'react-icons/ri';
import { toast } from 'react-toastify';
import AttToken from '../../helpers/attToken';
import { trackEventMatomo } from '../../helpers/matomo';
import Anonimo from '../../service/Anonimo';
import queryClient from '../../service/query';
import { ItemChecklist } from '../../types/checklist/types';
import { ChamadoType } from '../../types/new-form-filling/types';
import type { ChecklistReportType,TableRow } from '../../types/reports/types';
import { StyledDiv } from '../TableHelpers/TableStyle';
import ModalDetailPrint from './Modal/ModalDetailPrint';

const anonimoService = new Anonimo();
const currentDate = new Date();
const oneWeekAgo = new Date(currentDate);

function FilledAccordeon({
  execucoes,
  modelo
}: {
  execucoes: ChecklistReportType[],
  modelo: { label: string, value: string } | null
}) {
  const { t } = useTranslation('translation');

  const mapKeys = {
    inicio: t('Reports.columns.start'),
    fim: t('Reports.columns.end'),
    duracao: `${t('preenchimento.duracao')}`,
    preenchimento: `${t('preenchimento.preenchimentoQtd')}`,
    camposOcultos: `${t('preenchimento.hiddenFieldKey')}`,
    camposOcultosValor: `${t('preenchimento.hiddenFieldValue')}`
  };

  const [currentExecution, setCurrentExecution] = useState<ChecklistReportType | null>(null);
  const [openModal, setOpenModal] = useState(false);

  const printToPdf = () => {
    const table = document.getElementById("print_to_pdf");
    if (table) {
      html2canvas(table)?.then((canvas) => {
        const data = canvas?.toDataURL();
        const pdfExportSetting = {
          PageOrientation: 'landscape',
          content: [
            {
              pageSize: 'a4',
              compress: true,
              image: data,
              width: 400,
            }
          ]
        };
        pdfMake?.createPdf(pdfExportSetting)?.download(`${t('Filling.report')} Axyma Forms.pdf`);
      });
    }
  };

  const columns = [
    {
      name: "inicio",
      label: `${t('preenchimento.visualizar')}`,
      options: {
        display: false,
        filter: false,
        sort: true,
        sortThirdClickReset: true
      }
    },
    {
      name: "inicio",
      label: `${t('preenchimento.iniciadoEm')}`,
      options: {
        display: true,
        customBodyRender: (value: number) => {
          return (
            <span>
              {new Date(value)?.toLocaleString('pt-br') || null}
            </span>);
        },
        filter: true,
        sort: true,
        sortThirdClickReset: true
      }
    },
    {
      name: "fim",
      label: `${t('preenchimento.FinalizadoEm')}`,
      options: {
        display: true,
        customBodyRender: (value: number) => {
          return <span>
            {new Date(value)?.toLocaleString('pt-br') || null}
          </span>;
        },
        filter: true,
        sort: true,
        sortThirdClickReset: true
      }
    },
    {
      name: "duracao",
      label: `${t('preenchimento.duracao')}`,
      options: {
        display: true,
        filter: true,
        sort: true,
        sortThirdClickReset: true
      }
    },
    {
      name: "preenchimento",
      label: `${t('preenchimento.preenchimentoQtd')}`,
      options: {
        display: true,
        filter: true,
        sort: true,
        sortThirdClickReset: true
      }
    },
    {
      name: "subItemIdentificador",
      label: `${t('Filling.subitemIdentifier')}`,
      options: {
        filter: false,
        sort: true,
        sortThirdClickReset: true
      }
    },
    {
      name: "identificador",
      label: `${t('Filling.identifier')}`,
      options: {
        filter: false,
        sort: true,
        sortThirdClickReset: true
      }
    },
    {
      name: "ocultosFormatados",
      display: true,
      label: `${t('Filling.hiddenFields')}`,
      options: {
        customBodyRender: (value: string[]) => {
          if (value) {
            return value?.map((_val, index) => {
              if (index % 2 === 0) {
                return (
                  <div style={{ margin: '5px' }}>
                    <span className='tag is-dark'>{value[index]}:</span>
                    <span style={{ fontWeight: 'bold' }}> {value[index + 1]}</span>
                  </div>
                );
              } else {
                return <></>;
              }
            });
          } else return <></>;
        },
        filter: false,
        sort: true,
        sortThirdClickReset: true
      },
    },
    {
      name: "chamados",
      display: true,
      label: t('Filling.ticketsIssuedAtRoot'),
      options: {
        customBodyRender: (value: ChamadoType[]) => {
          return (
            <>
              {value && value?.length ? (
                <span>{`${value?.length} ${value?.length > 1 ? t('Filling.ticketsIssuedAtRoot') : t('Filling.ticketIssuedAtRoot')}`}</span>
              ) : (<span>{t('Filling.noTicketsIssuedAtRoot')}</span>)}
            </>
          );
        },
        filter: false,
        sort: true,
        sortThirdClickReset: true,
        sortCompare: (order: 'asc' | 'desc' | 'none') => {
          return (obj1: { data: ChamadoType[] }, obj2: { data: ChamadoType[] }) => {
            const obj1Length = obj1?.data ? obj1?.data?.length : 0;
            const obj2Length = obj2?.data ? obj2?.data?.length : 0;
            if (order === 'asc') {
              return obj2Length - obj1Length;
            } else {
              return obj1Length - obj2Length;
            }
          };
        }
      }
    },
    {
      name: "itens",
      display: true,
      label: t('Filling.ticketsIssuedInSubitems'),
      options: {
        customBodyRender: (value: ItemChecklist[]) => {
          if (value && value?.length) {
            let chamadosAbertos = 0;
            value?.forEach(e => e?.subitens?.forEach(sub => {
              if (sub?.chamados && sub?.chamados?.length) { chamadosAbertos += sub?.chamados?.length; }
            }));
            return (
              <>
                {chamadosAbertos ? (
                  <span>{`${chamadosAbertos} ${chamadosAbertos > 1 ? t('Filling.ticketsIssuedInSubitems') : t('Filling.ticketIssuedInSubitems')}`}</span>
                ) : (<span>{t('Filling.noTicketsIssuedInSubitems')}</span>)}
              </>
            );
          } else {
            return <></>;
          }
        },
        filter: false,
        sort: true,
        sortThirdClickReset: true,
        sortCompare: (order: 'asc' | 'desc' | 'none') => {
          return (obj1: { data: ItemChecklist[] }, obj2: { data: ItemChecklist[] }) => {
            let obj1Length = 0;
            let obj2Length = 0;
            obj1?.data?.forEach(e => e?.subitens?.forEach(sub => {
              if (sub.chamados && sub.chamados.length) { obj1Length += sub.chamados.length; }
            }));
            obj2?.data?.forEach(e => e?.subitens?.forEach(sub => {
              if (sub.chamados && sub.chamados.length) { obj2Length += sub.chamados.length; }
            }));
            if (order === 'asc') {
              return obj2Length - obj1Length;
            } else {
              return obj1Length - obj2Length;
            }
          };
        }
      },
    }
  ];

  const translatedTextLabels: MUIDataTableOptions = {
    onRowClick: (rowData) => {
      const execution = execucoes?.find(e => e.inicio === Number(rowData[0])) || null;
      if (execution)
        setCurrentExecution(execution);
      setOpenModal(true);
    },
    textLabels: {
      body: {
        noMatch: t('table.noMatch'),
        toolTip: t('table.toolTip'),
        columnHeaderTooltip: column => `${t('table.columnHeaderTooltip')} ${column.label}`
      },
      pagination: {
        next: t('table.next'),
        previous: t('table.previous'),
        rowsPerPage: t('table.rowsPerPage'),
        displayRows: t('table.displayRows')
      },
      toolbar: {
        search: t('table.search'),
        filterTable: t('table.filterTable')
      },
      filter: {
        title: t('table.title'),
        reset: t('table.reset')
      },
      viewColumns: {
        title: t('table.viewColumnsTitle')
      },
    },
  };

  const tableOptions: MUIDataTableOptions = {
    selectableRowsHeader: false,
    selectableRowsHideCheckboxes: true,
    customToolbar: () => {
      return (
        <>
          <Tooltip title={'Download PDF'}>
            <IconButton size='small' onClick={printToPdf}>
              <FaFilePdf />
            </IconButton>
          </Tooltip>
          <Tooltip title={'Download Excel'}>
            <IconButton
              size='small'
              onClick={() => {
                trackEventMatomo('preenchidos', 'click', 'button', 'gera csv');
                const current = execucoes as ChecklistReportType[];
                const bodyArray: Array<Record<string, unknown>> = [];
                const headerArray: string[] = [];
                (Object.keys(mapKeys) as Array<keyof typeof mapKeys>)?.forEach(key => {
                  headerArray?.push(mapKeys[key as keyof typeof mapKeys]);
                });
                current?.forEach((elem) => {
                  const objToInstert: Record<string, unknown> = {};
                  const elemRecord = elem as Record<string, unknown>;
                  (Object.keys(mapKeys) as Array<keyof typeof mapKeys>)?.forEach((key) => {
                    if (key === 'camposOcultos') {
                      objToInstert['camposOcultos'] = String(Object?.keys(elemRecord[key] || {}))?.replaceAll(',', ' | ');
                      objToInstert['camposOcultosValor'] = String(Object?.values(elemRecord[key] || {}))?.replaceAll(',', ' | ');
                    } else if (key === 'inicio' || key === 'fim') {
                      objToInstert[key] = new Date(Number(elemRecord[key]))?.toLocaleString('pt-br')?.replace(',', ' ');
                    } else if (key !== 'camposOcultosValor') {
                      objToInstert[key] = elemRecord[key];
                    }
                  }
                  );
                  bodyArray.push(objToInstert);
                });
                downloadExcel({
                  fileName: modelo?.label as string,
                  sheet: "react-export-table-to-excel",
                  tablePayload: {
                    header: headerArray || [''],
                    body: bodyArray as unknown as TableRow[],
                  },
                });
              }}>
              <RiFileExcel2Fill />
            </IconButton>
          </Tooltip>
        </>
      );
    },
    downloadOptions: {
      filename: modelo?.label as string,
    },
    onDownload: (buildHead, buildBody, columns, data) => {
      const newData = data?.map((ele: { data: (string | number | Record<string, unknown> | string[] | unknown[] | null | undefined)[] }) => {
        ele.data[0] = new Date(ele.data[0] as string | number | Date)?.toLocaleString('pt-br');
        ele.data[1] = new Date(ele.data[1] as string | number | Date)?.toLocaleString('pt-br');
        ele.data[5] = Object?.keys((ele.data[6] as Record<string, unknown>) || {});
        ele.data[6] = Object?.values((ele.data[6] as Record<string, unknown>) || {});
        return ele;
      });
      return "\uFEFF" + buildHead(columns) + buildBody(newData);
    },
    print: true,
    download: true,
    filter: false,
    filterType: 'textField',
    responsive: `vertical`,
    rowsPerPageOptions: [10, 15, Number(execucoes?.length)]
  };

  const getMuiTheme = () => createTheme({
    components: {
      MuiFormGroup: {
        styleOverrides: {
          root: {
            "&>:nth-child(1)": {
              display: 'none'
            }
          }
        }
      },
      MuiInputBase: {
        styleOverrides: {
          input: {
            width: 'fit-content',
            margin: 0,
            padding: 0
          }
        }
      },
      MuiList: {
        styleOverrides: {
          root: {
            display: 'flex',
            flexDirection: 'column',
          }
        }
      },
      MuiTableBody: {
        styleOverrides: {
          root: {
            td: {
              height: 'fit-content',
              margin: 0,
              padding: '3px 10px',
              width: 'fit-content'
            },
          }
        }
      },
      MuiGrid: {
        styleOverrides: {
          root: {
            margin: 0,
            padding: 0
          }
        }
      },
      MuiFormControl: {
        styleOverrides: {
          root: {
            padding: '10px'
          }
        }
      },
    },
  });

  return (
    <>
      {openModal && currentExecution && (
        <ModalDetailPrint
          numberOfTicktes={currentExecution.chamadosConfig?.limiteChamado}
          selected={[currentExecution]}
          openModal={openModal}
          setOpenModal={setOpenModal}
          commingFromFilled={true} />
      )}
      {modelo && execucoes?.length
        ? <StyledDiv theme={JSON.stringify(window.screen.width)}
          id="print_to_pdf"
          style={{ width: '100%' }}>
          <ThemeProvider theme={getMuiTheme()}>
            <MUIDataTable
              title={<Typography color='textDisabled' component={'p'}>
                {t('preenchimento.execucoes')}:
                <Typography fontWeight={600} component={'span'} sx={{ wordBreak: 'break-word' }}>
                  &nbsp;{modelo?.label ?? ''}
                </Typography>
              </Typography>
              }
              data={execucoes ?? []}
              columns={columns}
              options={{ ...tableOptions, ...translatedTextLabels }}
            />
          </ThemeProvider>
        </StyledDiv>
        : <Typography fontWeight={600} fontSize='large' color='textDisabled' textAlign='center'>{t('table.noMatch')}</Typography>}
    </>
  );
}

export default function FilledForms() {
  const [modelo, setModelo] = useState<{ label: string, value: string } | null>(null);
  const [inicio, setInicio] = useState<Date | number | null>(oneWeekAgo.setDate(currentDate.getDate() - 7));
  const [final, setFinal] = useState<Date | number | null>(new Date());
  const [hasSearched, setHasSearched] = useState(false);
  const { t } = useTranslation('translation');

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['GET_MODELOS_ANONIMOS'],
    queryFn: async () => {
      const promise = toast.promise(async () => {
        const token = await AttToken();
        if (token) {
          const response = await anonimoService.listaModelosChecklistAnonimo(token);
          if (!response || response === 403) throw new Error('erro ao buscar modelos');
          return Array.isArray(response) ? response : [];
        }
      }, {
        pending: t('Filling.formFilling.buscaModelos'),
        success: t('Filling.formFilling.modelosEncontrados'),
        error: t('Filling.formFilling.erroBuscaModelos')
      }, { toastId: 'busca modelos' });
      return promise;
    },
    retry: 2,
    refetchOnWindowFocus: false,
    refetchOnMount: 'always',
    gcTime: 5 * 60 * 1000,
    staleTime: 5 * 60 * 1000
  });

  const { data: execucoes, isLoading: loadingExecutions, isFetching: fetchingExecutions, refetch } = useQuery({
    queryKey: ['GET_EXECUTION', modelo],
    queryFn: async () => {
      const promise = toast.promise(async () => {
        const token = await AttToken();
        if (token) {
          const response = await anonimoService.listaHistoricoAnonimo(
            token,
            modelo?.value ?? '',
            new Date(inicio as Date).getTime(),
            new Date(final as Date).getTime());
          if (!response || response?.error) throw new Error('erro ao buscar execução');
          return Array.isArray(response) ? response : [];
        }
      }, {
        pending: t('Filling.formFilling.buscaExec'),
        success: t('Filling.formFilling.execEncontrada'),
        error: t('Filling.formFilling.erroBuscaExex')
      }, { toastId: 'busca execução' });
      if (!promise) throw new Error('erro na promise');
      return promise;
    },
    retry: 2,
    refetchOnWindowFocus: false,
    refetchOnMount: 'always',
    gcTime: 5 * 60 * 1000,
    staleTime: 5 * 60 * 1000,
    enabled: false
  });

  const onLoading = isLoading || isFetching || loadingExecutions || fetchingExecutions;

  return (
    <Stack direction='column' width='100%' justifyContent='flex-start' alignItems='flex-start' spacing={2}>
      <Autocomplete
        fullWidth
        disabled={onLoading}
        onClick={() => trackEventMatomo('preenchidos', 'click', 'select', 'seleciona modelo')}
        noOptionsText={t('Filling.formFilling.noModelsAvailable')}
        filterSelectedOptions
        value={modelo}
        options={
          data
            ?.filter((item, index, self) =>
              index === self?.findIndex(obj => obj.id === item.id))
            ?.sort((a, b) => { return a?.nome?.toLowerCase() > b?.nome?.toLowerCase() ? 1 : -1; })
            ?.map((ele) => ({
              label: ele.nome,
              value: ele.id,
            })) ?? []}
        onChange={(_event, newValue: { label: string, value: string } | null, _reason) => {
          queryClient.resetQueries({ queryKey: ['GET_EXECUTION'] });
          setHasSearched(false);
          if (_reason === 'clear') {
            setModelo(null);
          } if (newValue)
            setModelo(newValue);
        }}
        renderOption={(props, option) => (
          <li {...props} key={option.value}>
            <span>{option.label}</span>
          </li>)}
        renderInput={(params) => <TextField {...params} label={t('Filling.formFilling.executionModels')} />}
      />
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        width='100%'
      >
        <Stack
          direction="row"
          justifyContent="flex-start"
          alignItems="flex-start"
          spacing={1}
        >
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
            <DesktopDatePicker
              disabled={onLoading}
              maxDate={new Date()}
              format='dd/MM/yyyy'
              label={t('Filling.start')}
              value={new Date(inicio as number)}
              onChange={(newValue) => {
                if (!newValue) return;
                trackEventMatomo('preenchidos', 'click', 'datepicker', 'seleciona data');
                const date = new Date(newValue.getTime());
                date.setHours(0, 0, 0, 0);
                setInicio(date);
              }}
            />
            <DesktopDatePicker
              disabled={onLoading}
              format='dd/MM/yyyy'
              label={t('Filling.final')}
              value={new Date(final as number)}
              minDate={new Date(inicio as number)}
              maxDate={new Date()}
              onChange={(newValue) => {
                if (!newValue) return;
                trackEventMatomo('preenchidos', 'click', 'datepicker', 'seleciona data');
                const date = new Date(newValue.getTime());
                date.setHours(23, 59, 59, 59);
                setFinal(date);
              }}
            />
          </LocalizationProvider>
        </Stack>
        <Button
          endIcon={<IoFilterSharp />}
          onClick={() => {
            trackEventMatomo('preenchidos', 'click', 'button', 'pesquisar');
            setHasSearched(true);
            refetch();
          }}
          variant='contained'
          color='success'
          disabled={!modelo || ((final as Date) < (inicio as Date)) || onLoading}
        >
          {t('filter')}
        </Button>
      </Stack>
      {((final as Date) < (inicio as Date)) && <Typography variant='subtitle2'>{t('Filling.endDateCannotBeEarlierThanStartDate')}</Typography>}
      {loadingExecutions || fetchingExecutions ? (
        <Skeleton variant='rounded' height={600} width='100%' />
      ) : hasSearched ? (
        <FilledAccordeon
          modelo={modelo}
          execucoes={execucoes ?? []}
        />
      ) : null}
    </Stack>
  );
}